import { useEffect, useRef, useState } from "react";
import { X, Download, Share2 } from "lucide-react";
import { toast } from "sonner";

// Card canvas dimensions — 4:5 portrait, matches Instagram Story crop.
const W = 1080;
const H = 1350;

/**
 * Renders a 1080×1350 share image on an offscreen canvas and lets the
 * reader download it as a PNG or share via the native Web Share API.
 *
 * Props:
 *   - book: { title, author, cover_url, series_name? }
 *   - rating: 0-5 star rating to draw (integer or half-star float)
 *   - review: optional review text (truncated to ~180 chars in the render)
 *   - onClose: () => void
 */
export function ShareImageCard({ book, rating = 0, review = "", onClose }) {
  const canvasRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [pngUrl, setPngUrl] = useState("");

  useEffect(() => {
    if (!book) return;
    let alive = true;
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");
    render(ctx, book, rating, review).then(() => {
      if (!alive) return;
      setPngUrl(canvas.toDataURL("image/png"));
    });
    return () => { alive = false; };
  }, [book, rating, review]);

  const download = () => {
    if (!pngUrl) return;
    const a = document.createElement("a");
    a.href = pngUrl;
    a.download = `${(book.title || "reads").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-reads.png`;
    a.click();
  };

  const share = async () => {
    if (!pngUrl) return;
    setBusy(true);
    try {
      const blob = await (await fetch(pngUrl)).blob();
      const file = new File([blob], "reads-share.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: book.title, text: `${book.title} — ${book.author}` });
      } else {
        download();
      }
    } catch (e) {
      if (e.name !== "AbortError") toast.error("Couldn't share — try downloading instead");
    } finally {
      setBusy(false);
    }
  };

  if (!book) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4 py-8"
      onClick={onClose}
      data-testid="share-image-modal"
    >
      <div
        className="bg-[#FAF5E6] max-w-md w-full max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[rgba(10,10,10,0.10)] px-5 py-3">
          <div className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#4A4030]">Share card</div>
          <button onClick={onClose} className="p-1 -m-1 text-[#525252] hover:text-[#0A0A0A]" aria-label="Close" data-testid="share-image-close">
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
        <div className="p-5">
          <canvas ref={canvasRef} className="hidden" />
          {pngUrl ? (
            <img src={pngUrl} alt="Share card preview" className="w-full border border-[rgba(10,10,10,0.10)]" data-testid="share-image-preview" />
          ) : (
            <div className="w-full aspect-[4/5] flex items-center justify-center bg-[#F2EAD3] font-mono uppercase tracking-widest text-[10px] text-[#525252]">Rendering…</div>
          )}
          <div className="mt-5 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={download}
              disabled={!pngUrl}
              className="inline-flex items-center gap-1.5 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-[10px] px-4 py-2.5 rounded-sm transition-colors disabled:opacity-50"
              data-testid="share-image-download"
            >
              <Download className="w-3 h-3" strokeWidth={1.5}/> Download PNG
            </button>
            <button
              type="button"
              onClick={share}
              disabled={!pngUrl || busy}
              className="inline-flex items-center gap-1.5 border border-[rgba(10,10,10,0.18)] bg-[#F2EAD3] hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-[10px] px-4 py-2.5 rounded-sm transition-colors disabled:opacity-50"
              data-testid="share-image-share"
            >
              <Share2 className="w-3 h-3" strokeWidth={1.5}/> {busy ? "Sharing…" : "Share…"}
            </button>
          </div>
          <p className="mt-3 text-[11px] text-[#525252] leading-relaxed">
            1080×1350 · Instagram-story friendly. On phones, tap Share to send directly to Instagram/WhatsApp/Messages. On desktop, download and drag into your post.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Canvas rendering — no external deps, just Canvas 2D + a helper for word wrap
// ---------------------------------------------------------------------------

async function render(ctx, book, rating, review) {
  // Cream background.
  ctx.fillStyle = "#F0EAD6";
  ctx.fillRect(0, 0, W, H);

  // Subtle noise grain via diagonal pattern.
  ctx.fillStyle = "rgba(10,10,10,0.02)";
  for (let i = 0; i < H + W; i += 6) {
    ctx.fillRect(0, i, W, 1);
  }

  // Card container — inset with a thin border.
  const pad = 64;
  ctx.strokeStyle = "rgba(10,10,10,0.14)";
  ctx.lineWidth = 2;
  ctx.strokeRect(pad, pad, W - pad * 2, H - pad * 2);

  // Cover — top area, centred.
  const coverW = 420;
  const coverH = 630;
  const coverX = (W - coverW) / 2;
  const coverY = 140;
  ctx.fillStyle = "#E8DFC1";
  ctx.fillRect(coverX, coverY, coverW, coverH);

  if (book.cover_url) {
    try {
      const img = await loadImage(book.cover_url);
      const scale = Math.max(coverW / img.width, coverH / img.height);
      const sw = coverW / scale;
      const sh = coverH / scale;
      const sx = (img.width - sw) / 2;
      const sy = (img.height - sh) / 2;
      ctx.drawImage(img, sx, sy, sw, sh, coverX, coverY, coverW, coverH);
    } catch { /* cover load failed — leave the placeholder colour */ }
  }
  // Cover border on top of image.
  ctx.strokeStyle = "rgba(10,10,10,0.20)";
  ctx.lineWidth = 3;
  ctx.strokeRect(coverX, coverY, coverW, coverH);

  // Small "reads" wordmark, top-right corner of the card, tiny.
  ctx.fillStyle = "#0A0A0A";
  ctx.font = "700 22px 'Playfair Display', Georgia, serif";
  ctx.textAlign = "right";
  ctx.fillText("R", W - pad - 20, pad + 42);
  ctx.font = "500 14px 'IBM Plex Mono', monospace";
  ctx.fillStyle = "#4A4030";
  ctx.fillText("READS", W - pad - 42, pad + 40);

  // Series (optional), above title.
  const textY0 = coverY + coverH + 60;
  let cursorY = textY0;
  ctx.textAlign = "center";
  if (book.series_name) {
    ctx.fillStyle = "#8B7100";
    ctx.font = "500 20px 'IBM Plex Mono', monospace";
    const seriesTxt = `${book.series_name.toUpperCase()}${book.series_index != null ? ` · #${book.series_index}` : ""}`;
    ctx.fillText(seriesTxt, W / 2, cursorY);
    cursorY += 38;
  }

  // Title — Playfair, heavy, with wrap.
  ctx.fillStyle = "#0A0A0A";
  ctx.font = "800 60px 'Playfair Display', Georgia, serif";
  cursorY = wrapText(ctx, book.title || "", W / 2, cursorY, W - pad * 2 - 60, 66);
  cursorY += 8;

  // Author.
  ctx.fillStyle = "#4A4030";
  ctx.font = "italic 28px 'Playfair Display', Georgia, serif";
  ctx.fillText(book.author || "", W / 2, cursorY);
  cursorY += 44;

  // Stars.
  cursorY += 8;
  drawStars(ctx, W / 2, cursorY, rating);
  cursorY += 60;

  // Review (optional) — small, single paragraph, truncated.
  if (review) {
    const excerpt = review.length > 180 ? review.slice(0, 177).trim() + "…" : review;
    ctx.fillStyle = "#0A0A0A";
    ctx.font = "400 22px Georgia, serif";
    ctx.fillText("“", W / 2, cursorY - 8);
    cursorY += 22;
    wrapText(ctx, excerpt, W / 2, cursorY, W - pad * 2 - 100, 30);
  }

  // Footer wordmark.
  ctx.fillStyle = "#525252";
  ctx.font = "500 16px 'IBM Plex Mono', monospace";
  ctx.textAlign = "center";
  ctx.fillText("READSCO.ORG", W / 2, H - pad - 32);
}

function drawStars(ctx, cx, cy, rating) {
  const size = 36;
  const gap = 12;
  const total = 5;
  const fullWidth = size * total + gap * (total - 1);
  let x = cx - fullWidth / 2 + size / 2;
  for (let i = 0; i < total; i++) {
    const fill = Math.max(0, Math.min(1, rating - i));
    star(ctx, x, cy, size / 2, fill);
    x += size + gap;
  }
}

function star(ctx, cx, cy, radius, fillFraction) {
  // 5-point star path.
  ctx.save();
  ctx.beginPath();
  const spikes = 5;
  const inner = radius * 0.45;
  for (let i = 0; i < spikes * 2; i++) {
    const r = i % 2 === 0 ? radius : inner;
    const a = (Math.PI / spikes) * i - Math.PI / 2;
    const px = cx + Math.cos(a) * r;
    const py = cy + Math.sin(a) * r;
    if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.strokeStyle = "#0A0A0A";
  ctx.lineWidth = 2.5;
  // Empty outline first.
  ctx.stroke();
  if (fillFraction > 0) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(cx - radius, cy - radius, radius * 2 * fillFraction, radius * 2);
    ctx.clip();
    ctx.fillStyle = "#8B7100";
    ctx.beginPath();
    for (let i = 0; i < spikes * 2; i++) {
      const r = i % 2 === 0 ? radius : inner;
      const a = (Math.PI / spikes) * i - Math.PI / 2;
      const px = cx + Math.cos(a) * r;
      const py = cy + Math.sin(a) * r;
      if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }
  ctx.restore();
}

function wrapText(ctx, text, cx, y, maxWidth, lineHeight) {
  const words = text.split(/\s+/);
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  // Cap at 3 lines for the title to keep the card composed.
  const capped = lines.length > 3 ? [...lines.slice(0, 2), lines[2].slice(0, -1) + "…"] : lines;
  capped.forEach((ln, i) => ctx.fillText(ln, cx, y + i * lineHeight));
  return y + capped.length * lineHeight;
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    // Add a query param to avoid CORS-cache issues; covers.openlibrary.org
    // sends `Access-Control-Allow-Origin: *`.
    img.src = src + (src.includes("?") ? "&" : "?") + "share=1";
  });
}
